import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, find } from 'lodash';
import Platform from 'platform';
import moment from 'moment';
import config from 'config';
import PLAYER_TYPES from 'constants/player-types';
import VIDEO_EVENTS from 'constants/video-events';
import GlobalLoader from 'components/Loader';
import { AVERAGE_LIVE_LATENCY } from 'config';
import ShakaPlayer from './ShakaPlayer';
import HLSPlayer from './HLSPlayer';
import WebOSPlayer from './VendorPlayers/WebOS';
import HisensePlayer from './VendorPlayers/Hisense';
import Controls from './Controls';
import { StyledPlayerWrapper } from './StyledComponents';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import ChannelLogo from './ChannelLogo';

const SEEK_TIMEOUT = 1000;
const REWIND_MAX_INTERVAL_MS = 800;

const playersByPlatform = {
  Browser: ShakaPlayer,
  Browser_HLS: HLSPlayer,
  Tizen: ShakaPlayer,
  Tizen_HLS: HLSPlayer,
  WebOS: ShakaPlayer,
  WebOS_HLS: HLSPlayer,
  Hisense: HisensePlayer,
  Hisense_HLS: HLSPlayer,
  Vewd: ShakaPlayer,
  Vewd_HLS: HLSPlayer,
};

/**
 * Returns a function that calculates an addition seek time of each click
 * @param {func} cb
 * @returns
 */
const createSeekFn = (cb) => {
  let countOfClicks = 0;
  let lastClickTimestamp = Date.now();

  return () => {
    if (!lastClickTimestamp || Date.now() - lastClickTimestamp < REWIND_MAX_INTERVAL_MS) {
      countOfClicks += 1;
      lastClickTimestamp = Date.now();
    } else {
      lastClickTimestamp = null;
      countOfClicks = 0;
    }

    let value = 0;

    if (countOfClicks >= 15) {
      // 5 minutes
      value = 5 * 60;
    } else if (countOfClicks >= 10) {
      // 2 minutes
      value = 2 * 60;
    } else if (countOfClicks >= 5) {
      // 30 seconds
      value = 30;
    } else {
      // 5 seconds
      value = 5;
    }

    cb(value);
  };
};

const NullComponent = () => null;

let VendorPlayer = NullComponent;

class Player extends Component {
  playerRef = createRef();

  seekTimeout = null;

  state = {
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    isSeeking: false,
    seekPending: null,
    isWaiting: false,
    isResetToStartAvailable: false,
    isSettingsAvailable: false,
    isSettingsOverlayOpen: false,
    useTimeshiftLiveStream: this.props.playerType === PLAYER_TYPES.TIMESHIFT,
    timeShiftStartTime: undefined,
    settings: {},
  };

  UNSAFE_componentWillMount() {
    const { isHLSPlayerRequired } = this.props;
    let playerName = `${Platform.getName()}${isHLSPlayerRequired ? '_HLS' : ''}`;

    //STVWL-289 if it's VEWD on old Hisense TV, use Hisense native player
    if (playerName === 'Vewd' && Platform.isHisenseTVStoreOldModel()) {
      VendorPlayer = HisensePlayer;
    } else {
      VendorPlayer = playersByPlatform[playerName] || NullComponent;
    }
  }

  componentDidMount() {
    document.addEventListener('visibilitychange', this.onVisibilityChangeHandler, false);
  }

  componentWillUnmount() {
    const { onPlayerClose } = this.props;
    // It should be called after childs "willUnmount" events (e.g. after VideoComponent)
    // because it completelly clears the store and breaks player status events
    setTimeout(() => onPlayerClose(), 0);
    document.removeEventListener('visibilitychange', this.onVisibilityChangeHandler, false);
  }

  componentDidUpdate(prevProps, prevState) {
    const { isSeeking } = this.state;
    const { isSeeking: prevIsSeeking } = prevState;
    if (this.props.error) {
      this.onClickPause();
      return;
    }
    if (prevIsSeeking && !isSeeking) {
      this.setState({ seekPending: null });
    }
    if (prevProps.src !== this.props.src && this.props.playerType === PLAYER_TYPES.TIMESHIFT) {
      this.setState({ useTimeshiftLiveStream: true, timeShiftStartTime: undefined });
    }
  }

  onVisibilityChangeHandler = () => {
    const player = this.playerRef.current;
    if (!player) return;
    window.document.hidden ? this.onClickPause() : this.onClickPlay();
  };

  onPlayerOpen = () => {
    const { queryParams, playerType, startFrom, onPlayerOpen } = this.props;
    onPlayerOpen({ queryParams, playerType, startFrom });
  };

  onTimeUpdate = () => {
    const player = this.playerRef.current;
    const { playerType, onTimeUpdate } = this.props;
    const { duration, useTimeshiftLiveStream } = this.state;
    let currentTime = player.getCurrentTime();

    if (playerType === PLAYER_TYPES.TIMESHIFT && useTimeshiftLiveStream) {
      currentTime = this.getCurrentLiveTime();
    }
    const timeToResetToStart = 60;
    const isResetToStartAvailable = playerType !== PLAYER_TYPES.CHANNEL && currentTime >= timeToResetToStart;

    if (currentTime === undefined || Number.isNaN(currentTime)) return;
    if (currentTime > duration && playerType === PLAYER_TYPES.TIMESHIFT) {
      return this.onEnded();
    }

    this.setState({ currentTime, isResetToStartAvailable });

    if (onTimeUpdate) onTimeUpdate(currentTime);
  };

  onEnded = () => {
    const { playerType, navigation, history, onEnded, content, changeProgram } = this.props;

    onEnded();

    if ([PLAYER_TYPES.VOD, PLAYER_TYPES.TRAILER].includes(playerType)) return history.goBack();

    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) {
      const hasAvailableNextProgram = !!navigation.nextProgram.startTime;
      hasAvailableNextProgram ? changeProgram(navigation.nextProgram) : history.goBack();
    }
    if (playerType === PLAYER_TYPES.SERIES) {
      const hasAvailableNextEpisode = !!navigation.nextEpisode;
      hasAvailableNextEpisode ? this.changeEpisode(navigation.nextEpisode) : history.goBack();
    }

    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      // It will update url with actual programStartTime in url
      this.changeChannel(content);
    }
  };

  onClickPlay = () => {
    const player = this.playerRef.current;

    player && player.play();
  };

  onClickPause = () => {
    const player = this.playerRef.current;
    this.sendInnerAnalyticsEvent('pause');

    player && player.pause();
  };

  onClickRewind = createSeekFn((value) => {
    const { seekPending, currentTime } = this.state;
    let newSeek = 0;

    if (seekPending !== null) {
      newSeek = seekPending - value;
    } else {
      newSeek = currentTime - value;
    }

    this.setState({
      seekPending: newSeek >= 0 ? newSeek : 0,
    });

    this.seekAfterTimeout();
  });

  onClickFF = createSeekFn((value) => {
    const { duration, seekPending, currentTime } = this.state;
    const { playerType, timeshiftBegin } = this.props;
    const currentLiveTime = moment(Date.now()).diff(timeshiftBegin, 'seconds');
    const newSeek = (seekPending || currentTime) + value;

    const endOfSeekParam = PLAYER_TYPES.TIMESHIFT === playerType ? currentLiveTime : duration;

    // We can't set durection = newSeek, cause player doesn't trigger onEnded in this case, so -1 second hack
    const newSeekValue = newSeek <= endOfSeekParam ? newSeek : endOfSeekParam - 1;

    this.setState({
      seekPending: newSeekValue,
    });

    this.seekAfterTimeout();
  });

  onClickSettings = () => {
    const { showOverlay } = this.props;
    const { settings } = this.state;
    const { quality, language } = settings;

    this.sendInnerAnalyticsEvent('click settings');

    const onSelect = (data) => {
      if (data.type === 'audioTrack') {
        this.sendInnerAnalyticsEvent('settings', `set video language:${data.id}`);
        const updated = language.map((lang) => {
          return { ...lang, isActive: lang.id === data.id };
        });

        this.setState({
          settings: { ...settings, language: updated },
        });
      }
      if (data.type === 'videoQuality') {
        this.sendInnerAnalyticsEvent('settings', `set stream quality:${data.id}`);

        const updated = quality.map((q) => {
          return { ...q, isActive: q.id === data.id };
        });

        this.setState({
          settings: { ...settings, quality: updated },
        });
      }
    };

    // Лайфхак, чтобы показались контролы после скрытия настроек с фокусом на кнопке Play.
    const onClose = () => {
      this.setState({ isSettingsOverlayOpen: false });
      this.onClickPlay();
    };

    this.setState({ isSettingsOverlayOpen: true });

    showOverlay({
      type: 'player-settings',
      returnFocusTo: '@PLAYER_CONTROLS',
      options: { quality, language, onSelect, onClose },
    });
  };

  onClickPrev = () => {
    const playerInstance = this.playerRef.current;
    const { playerType, navigation, changeProgram } = this.props;
    const { isResetToStartAvailable, useTimeshiftLiveStream } = this.state;
    // reset to start has more priority
    if (isResetToStartAvailable) {
      this.sendInnerAnalyticsEvent('restart');
      if (playerType === PLAYER_TYPES.TIMESHIFT && useTimeshiftLiveStream) {
        this.resolveTimeshiftSeek(0);
        return;
      }
      return playerInstance.setCurrentTime(1);
    }

    if (
      [PLAYER_TYPES.CATCHUP, PLAYER_TYPES.CHANNEL, PLAYER_TYPES.TIMESHIFT, PLAYER_TYPES.VOD_CHANNEL].includes(
        playerType,
      )
    ) {
      this.sendInnerAnalyticsEvent('previous event');
      changeProgram(navigation.prevProgram);
    }
    if (playerType === PLAYER_TYPES.SERIES) {
      this.sendInnerAnalyticsEvent('previous episode');
      this.changeEpisode(navigation.prevEpisode);
    }
  };

  onClickNext = () => {
    const { playerType, navigation, changeProgram } = this.props;
    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.CHANNEL, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) {
      this.sendInnerAnalyticsEvent('next event');
      changeProgram(navigation.nextProgram);
    }
    if (playerType === PLAYER_TYPES.TIMESHIFT) this.onClickOnAir();

    if (playerType === PLAYER_TYPES.SERIES) {
      this.sendInnerAnalyticsEvent('next episode');
      this.changeEpisode(navigation.nextEpisode);
    }
  };

  onClickOnAir = () => {
    const { playerType, navigation, content, navigateToPlayerPage, timeshiftBegin } = this.props;

    if ([PLAYER_TYPES.CATCHUP].includes(playerType)) {
      this.sendInnerAnalyticsEvent('on air button');
      if (navigation && navigation.currentChannel) {
        this.changeChannel(navigation.currentChannel);
      } else {
        // При переходе из Истории просмотра у нас нет навигации, а кнопка "назад в эфир" есть
        navigateToPlayerPage({
          item: { id: content.channelId },
          type: PLAYER_TYPES.CHANNEL,
          useReplace: true,
        });
      }
    }

    // We can click onAir only when timeshift isn't playing live steam.
    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      const queryBasedProgram = content?.epg?.find(({ startTime }) => startTime === timeshiftBegin);
      // if channel has different live program that means program we're playing right now has ended.
      const isCurrentProgramEnded = queryBasedProgram?.id !== content.currentProgram?.id;
      this.sendInnerAnalyticsEvent('on air button');

      if (isCurrentProgramEnded) {
        this.changeChannel(content);
      } else {
        this.setState({ useTimeshiftLiveStream: true });
      }
      // If user clicked onAir - button (in TIMESHIFT player) would disappear
      // So need to set default focus instead
      SpatialNavigationWrapper.focus();
    }

    if (playerType === PLAYER_TYPES.VOD_CHANNEL) {
      const { currentChannel } = navigation;
      const { currentProgram } = currentChannel || {};

      const isLiveProgramPlaying = currentProgram?.id === content?.id;

      if (isLiveProgramPlaying) {
        const now = moment().add(config.timeDiffFromServer, 'ms');
        const seekPending = (now - moment(content.startTime)) / 1000;

        this.setState({ seekPending });

        this.seekAfterTimeout();
      } else {
        navigateToPlayerPage({
          item: currentChannel,
          type: PLAYER_TYPES.CHANNEL,
          useReplace: true,
        });
      }
      // If user clicked onAir - button (in TIMESHIFT and VOD_CHANNEL player) would disappear
      // So need to set default focus instead
      SpatialNavigationWrapper.focus();
    }
  };

  onClickChannelUp = () => {
    const { isChannelChangeAllowed, navigation, isEPGOpen } = this.props;

    if (isChannelChangeAllowed && !isEPGOpen) this.changeChannel(navigation.prevChannel);
  };

  onClickChannelDown = () => {
    const { isChannelChangeAllowed, navigation, isEPGOpen } = this.props;

    if (isChannelChangeAllowed && !isEPGOpen) this.changeChannel(navigation.nextChannel);
  };

  onClickProgress = (timestamp) => {
    const { playerType } = this.props;
    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      this.setState({ seekPending: timestamp });
      this.seekAfterTimeout();
      return;
    }
    const player = this.playerRef.current;
    if (player) player.setCurrentTime(timestamp);
  };

  onVideoEvent = (type, payload) => {
    const { onStartPlaying, onDurationChange, onStopPlaying, onError, playerType } = this.props;
    const { isPlaying } = this.state;
    const player = this.playerRef.current;

    switch (type) {
      case VIDEO_EVENTS.TIME_UPDATE:
        this.onTimeUpdate();
        if (isPlaying) {
          this.setState({ isWaiting: false });
        }
        break;
      case VIDEO_EVENTS.LOADED_METADATA:
        let duration = player.getDuration();
        if (playerType === PLAYER_TYPES.TIMESHIFT) {
          duration = this.calculateTimeshiftPlayerDuration();
        }
        this.setState({ duration });
        onDurationChange && onDurationChange(duration);
        break;
      case VIDEO_EVENTS.PLAY:
        onStartPlaying(this.state.currentTime);
        break;
      case VIDEO_EVENTS.PAUSE:
        // Prevent unnecessary "stop" event
        if (!isPlaying) break;
        this.setState({ isPlaying: false, isPaused: true });
        onStopPlaying(this.state.currentTime);
        break;
      case VIDEO_EVENTS.WAITING:
      case VIDEO_EVENTS.LOAD_START:
        this.setState({ isWaiting: true });
        break;
      case VIDEO_EVENTS.SEEKING:
        this.setState({ isSeeking: true });
        break;
      case VIDEO_EVENTS.SEEKED:
        this.setState({ isSeeking: false });
        break;
      case VIDEO_EVENTS.PLAYING:
        this.setState({ isPlaying: true, isWaiting: false, isPaused: false });
        break;
      case VIDEO_EVENTS.CAN_PLAY:
        this.onClickPlay();
        break;
      case VIDEO_EVENTS.ENDED:
        this.onEnded();
        break;
      case VIDEO_EVENTS.ERROR:
        onError(payload);
        break;
      default:
        break;
    }
  };

  calculateTimeshiftPlayerDuration = () => {
    const { content, timeshiftBegin } = this.props;
    const { startTime, endTime } = find(content?.epg, { startTime: timeshiftBegin }) || {};

    return moment(endTime).diff(startTime, 'seconds');
  };

  seekAfterTimeout = () => {
    clearTimeout(this.seekTimeout);
    this.seekTimeout = setTimeout(() => {
      const player = this.playerRef.current;
      const { playerType } = this.props;
      const { seekPending } = this.state;

      if (playerType === PLAYER_TYPES.TIMESHIFT) {
        this.resolveTimeshiftSeek(seekPending);
        return;
      }
      // We can leave player during rewind, so need to make sure that it exists
      if (player) player.setCurrentTime(seekPending);
      this.sendInnerAnalyticsEvent('rewind');
    }, SEEK_TIMEOUT);
  };

  // Dispatches a pending timeshift seek: switches to catchup (leaving live), switches back
  // to live, or - if staying live and still within AVERAGE_LIVE_LATENCY - just seeks in place.
  resolveTimeshiftSeek = (seekPending) => {
    const { useTimeshiftLiveStream } = this.state;
    const player = this.playerRef.current;
    const currentLiveTime = this.getCurrentLiveTime();

    if (useTimeshiftLiveStream) {
      const isLeavingLive = currentLiveTime > seekPending;
      if (isLeavingLive) {
        this.setState({ useTimeshiftLiveStream: false, timeShiftStartTime: seekPending });
        return;
      }
      // Still within latency of live edge - nothing to seek to, just clear the pending UI state.
      this.setState({ seekPending: null });
    } else {
      const isEnteringLive = currentLiveTime <= seekPending;
      if (isEnteringLive) {
        this.setState({ useTimeshiftLiveStream: true, timeShiftStartTime: undefined });
        return;
      }
      // We can leave player during rewind, so need to make sure that it exists
      if (player) player.setCurrentTime(seekPending);
    }
  };

  getCurrentLiveTime = () => {
    const { timeshiftBegin, playerType, navigation, content } = this.props;

    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      const currentLiveTime = moment(Date.now()).diff(timeshiftBegin, 'seconds');

      return Math.max(currentLiveTime - AVERAGE_LIVE_LATENCY, 0);
    }

    if (playerType === PLAYER_TYPES.VOD_CHANNEL && content) {
      const { currentProgram } = navigation?.currentChannel || {};
      const now = moment().add(config.timeDiffFromServer, 'ms');

      const isLiveProgramPlaying = currentProgram?.id === content.id;

      return isLiveProgramPlaying ? (now - moment(content.startTime)) / 1000 : 0;
    }

    return 0;
  };

  getIsLive = () => {
    const { playerType, content } = this.props;
    const { currentTime, useTimeshiftLiveStream } = this.state;

    if (playerType === PLAYER_TYPES.CHANNEL) return true;

    if (playerType === PLAYER_TYPES.VOD_CHANNEL && content) {
      const now = moment().add(config.timeDiffFromServer, 's');
      const isLiveProgram = moment(content.startTime) < now && moment(content.endTime) > now;
      const diff = Math.abs(moment(content.startTime) + currentTime * 1000 - now) / 1000;

      return isLiveProgram && diff <= AVERAGE_LIVE_LATENCY;
    }

    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      return !!useTimeshiftLiveStream;
    }

    return false;
  };

  onSettingsChange = (settings) => {
    this.setState({
      isSettingsAvailable: !isEmpty(settings.quality) || !isEmpty(settings.language),
      settings,
    });
  };

  changeChannel = (nextChannel) => {
    if (!nextChannel) return;

    const { navigateToPlayerPage } = this.props;

    navigateToPlayerPage({ item: nextChannel, type: PLAYER_TYPES.CHANNEL, useReplace: true });
  };

  changeEpisode = (nextEpisode) => {
    if (!nextEpisode) return;

    const { navigateToPlayerPage } = this.props;

    navigateToPlayerPage({ item: nextEpisode, type: PLAYER_TYPES.SERIES, useReplace: true });
  };

  sendInnerAnalyticsEvent = (type, eventValue) => {
    const { content, playerType } = this.props;
    if (!content) return;
    let addInfo = '';
    switch (playerType) {
      case PLAYER_TYPES.CATCHUP:
        addInfo = `vod_asset_id=${content.vodAssetId}; content_type=${content.state}; channel_id=${content.channelId}`;
        break;
      case PLAYER_TYPES.CHANNEL:
        addInfo = `channel_id=${content.id}`;
        break;
      default:
        addInfo = `vod_asset_id=${content.id}`;
        break;
    }

    innerAnalyticsVolia.postEvent({
      eventGroup: 'player',
      eventName: type,
      addInfo: addInfo,
      eventValue,
    });
  };

  // TIMESHIFT is the only playerType that can point at either manifest, depending on
  // whether playback is currently at the live edge or rewound into catchup.
  getContentType = () => {
    const { playerType } = this.props;
    const { useTimeshiftLiveStream } = this.state;

    if (PLAYER_TYPES.CHANNEL === playerType) return 'channel';
    if (PLAYER_TYPES.TIMESHIFT === playerType && useTimeshiftLiveStream) return 'channel';
    if (PLAYER_TYPES.TIMESHIFT === playerType && !useTimeshiftLiveStream) return 'epg';
    if ([PLAYER_TYPES.CATCHUP, PLAYER_TYPES.VOD_CHANNEL].includes(playerType)) return 'epg';

    return 'vod';
  };

  getContentId = () => {
    const { content, playerType, timeshiftBegin } = this.props;
    const { useTimeshiftLiveStream } = this.state;

    const isMegogoStream = content?.assetId?.includes('MEGO');

    if (playerType === PLAYER_TYPES.TIMESHIFT && !useTimeshiftLiveStream) {
      const currentProgram = content?.epg?.find((program) => program.startTime === timeshiftBegin);
      return currentProgram?.id;
    }

    return isMegogoStream ? content?.assetId : content?.id;
  };

  getStartFrom = () => {
    const { playerType, startFrom } = this.props;
    const { useTimeshiftLiveStream, timeShiftStartTime } = this.state;

    if (playerType === PLAYER_TYPES.TIMESHIFT) {
      if (useTimeshiftLiveStream) return undefined;
      return timeShiftStartTime;
    }

    return startFrom;
  };

  render() {
    const {
      src,
      width,
      height,
      timeshiftBegin,
      verimatrixId,
      history,
      playerType,
      content,
      navigation,
      error,
      toggleInFavoriteStatus,
      setMenuIdle,
      showEPG,
      isLoggedIn,
      isEPGOpen,
    } = this.props;
    const {
      currentTime,
      duration,
      isPlaying,
      isPaused,
      seekPending,
      isWaiting,
      isResetToStartAvailable,
      isSettingsAvailable,
      isSettingsOverlayOpen,
      settings,
    } = this.state;
    const contentId = this.getContentId();
    const isContentReadyToPlay = content && contentId && !error;
    const isDRMSupported = Platform.getDeviceType() !== 'OLD_TV';
    const contentType = this.getContentType();
    const startFrom = this.getStartFrom();

    // only for timeshift player
    const currentLiveTime = this.getCurrentLiveTime();
    const isLive = this.getIsLive();

    // "id" could be 0
    const lang = find(settings.language, ['isActive', true])?.id ?? null;
    const quality = find(settings.quality, ['isActive', true])?.id ?? null;

    return (
      <StyledPlayerWrapper width={width} height={height}>
        {playerType === PLAYER_TYPES.VOD_CHANNEL && (
          <ChannelLogo isEPGOpen={isEPGOpen} channelMetadata={content?.channelMetadata} />
        )}
        {isContentReadyToPlay && (
          <VendorPlayer
            ref={this.playerRef}
            src={src}
            playerType={playerType}
            isDRMSupported={isDRMSupported}
            contentId={contentId}
            contentType={contentType}
            width={width}
            height={height}
            startFrom={startFrom}
            timeshiftBegin={timeshiftBegin}
            verimatrixId={verimatrixId}
            lang={lang}
            quality={quality}
            isLoggedIn={isLoggedIn}
            onPlayerOpen={this.onPlayerOpen}
            onSettingsChange={this.onSettingsChange}
            onVideoEvent={this.onVideoEvent}
          />
        )}
        <Controls
          playerType={playerType}
          content={content}
          navigation={navigation}
          currentTime={currentTime}
          currentLiveTime={currentLiveTime}
          duration={duration === Infinity ? null : duration}
          isPlaying={isPlaying}
          isPaused={isPaused}
          isLive={isLive}
          seekPending={seekPending}
          timeshiftBegin={timeshiftBegin}
          onBack={history.goBack}
          isResetToStartAvailable={isResetToStartAvailable}
          isSettingsAvailable={isSettingsAvailable}
          isSettingsOverlayOpen={isSettingsOverlayOpen}
          isLoggedIn={isLoggedIn}
          onClickProgress={this.onClickProgress}
          onClickOnAir={this.onClickOnAir}
          onClickPlay={this.onClickPlay}
          onClickPause={this.onClickPause}
          onClickRewind={this.onClickRewind}
          onClickFF={this.onClickFF}
          onClickPrev={this.onClickPrev}
          onClickNext={this.onClickNext}
          onClickChannelUp={this.onClickChannelUp}
          onClickChannelDown={this.onClickChannelDown}
          onClickSettings={this.onClickSettings}
          onEpisodeEnter={this.changeEpisode}
          toggleInFavoriteStatus={toggleInFavoriteStatus}
          onClickEPG={showEPG}
          setMenuIdle={setMenuIdle}
          isVisible={!isEPGOpen}
        />
        {!isEPGOpen && isWaiting && <GlobalLoader cleanBackground={true} />}
      </StyledPlayerWrapper>
    );
  }
}

Player.defaultProps = {
  width: 1920,
  height: 1080,
};

Player.propTypes = {
  playerType: PropTypes.oneOf(Object.values(PLAYER_TYPES)).isRequired,
  onPlayerOpen: PropTypes.func.isRequired,
  onStartPlaying: PropTypes.func.isRequired,
  onStopPlaying: PropTypes.func.isRequired,
  onTimeUpdate: PropTypes.func.isRequired,
  onEnded: PropTypes.func.isRequired,
  navigateToPlayerPage: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  queryParams: PropTypes.object.isRequired,
  content: PropTypes.object,
  navigation: PropTypes.object,
  startFrom: PropTypes.number,
  isChannelChangeAllowed: PropTypes.bool,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default Player;
